"use strict";

import Collection from 'ol/Collection';
import Modify from 'ol/interaction/Modify';
import PointerInteraction from 'ol/interaction/Pointer';
import Vector from "ol/source/Vector";
import {unByKey} from 'ol/Observable';
import Overlay from "ol/Overlay";
import * as olCondition from 'ol/events/condition';
import * as olExtent from 'ol/extent';
import {fromExtent} from 'ol/geom/Polygon';
import Point from "ol/geom/Point";
import {Style, Circle, Fill, Stroke, RegularShape} from 'ol/style';
import jQuery from 'jquery';

/**
 *
 * @type {{BOX: string, SCALE: string, SCALE_V: string, SCALE_H: string, ROTATE: string, TRANSLATE: string}}
 */
gc.gis.map.OLMapTransformInteractionHandles =
{
	BOX:        'box',
	SCALE:      'scale',
	SCALE_V:    'scale_v',
	SCALE_H:    'scale_h',
	ROTATE:     'rotate',
	TRANSLATE:  'translate'
};

/**
 * @classdesc
 * Openlayers Implementierung für Geometrie-Bearbeitungsfunktionen.
 *
 * @abstract
 * @class
 * @extends gc.gis.map.GCMapTransformInteraction
 * @memberof gc.gis.map
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCMapView} map Karte, der die Interaktion gehört
 * @param {Object} [opt_options] optionale Aufrufoptionen
 * @param {boolean} [opt_options.allowClearDrawingArea=false] Flag welches anzeigt, ob die Inhalte des Tracking-Layer
 *      durch die Funktion "Zeichenfläche leeren", verändert werden dürfen
 * @param {gc.gis.action.GCAbstractAction} [opt_options.clearDrawingAreaAction] Spezielle Aktion die ausgeführt wird, wenn die Funktion
 *    "Zeichenfläche leeren", für den Layer ausgelöst wird (Voraussetzung: das Flag
 *    opt_options.allowClearDrawingArea wurde gesetzt
 * @param {gc.gis.style.GCAbstractStyle[]|Function|ol.style.Style} [opt_options.selectStyle] Style für ein selektiertes Objekt (dieser Style wird bei Bedarf direkt dem Feature zugewiesen)
 * @param {gc.gis.style.GCAbstractStyle[]|Function|ol.style.Style} [opt_options.hoverStyle] Style für ein gehovertes Objekt (dieser Style wird bei Bedarf direkt dem Feature zugewiesen)
 * @param {gc.gis.action.GCAbstractAction} [opt_options.saveAction] Aktion für speichern.
 * @param {gc.gis.action.GCAbstractAction} [opt_options.deleteAction] Aktion für löschen.
 * @param {gc.gis.action.GCAbstractAction} [opt_options.removeDrawAreaAction] Aktion für von Zeichenfläche entfernen.
 * @param {gc.gis.action.GCAbstractAction} [opt_options.cancelAction] Aktion für Bearbeitung beenden.
 */
gc.gis.map.OLMapTransformInteraction = function(map, opt_options)
{
	gc.gis.map.GCMapTransformInteraction.call(this, map, opt_options);

	opt_options = opt_options || {};

	this._DEF_PIXEL_TOLERANCE = 10;

	/**
	 * Style für selektiertes Feature.
	 * @type {ol.style.Style|null}
	 * @private
	 */
	this._selectStyle = opt_options.selectStyle ? opt_options.selectStyle : null;
	/**
	 * Style für hover Feature.
	 * @type {ol.style.Style|null}
	 * @private
	 */
	this._hoverStyle = opt_options.hoverStyle ? opt_options.hoverStyle : null;

	/**
	 * Liste von EventKeys, die beim Deaktivieren zurückgesetzt werden müssen.
	 * @type {Array}
	 * @private
	 */
	this._eventsKeyList = [];
	/**
	 * Liste der selektierten Features.
	 * @type {ol.Collection<ol.Feature>}
	 * @private
	 */
	this._selectedFeatures = new Collection();
	/**
	 * Liste mit zusätzlichen Objektreferenzen zu den selektierten Features.
	 * @type {ol.Collection<Object>}
	 * @private
	 */
	this._selectedFeaturesRefs = new Collection();
	/**
	 * @type {ol.Map}
	 * @private
	 */
	this._ol_map = this.map.getMap().getImpl();
	/**
	 * @type {ol.interaction.Modify}
	 * @private
	 */
	this._modifyInteraction = null;
	/**
	 * @type {ol.interaction.Pointer}
	 * @private
	 */
	this._pointerInteraction = null;
	/**
	 * Kapselt die _selectedFeatures-Collection. Zur Verwendung innerhalb der Interaktion und bietet komfortable
	 * Methoden zum Durchsuchen der Menge von Features.
	 * @type {ol.source.Vector}
	 * @private
	 */
	this._modifyInteractionSource = new Vector({
		features: this._selectedFeatures,
		useSpatialIndex: true
	});
	/**
	 * Merker für Cursordarstellung.
	 * @type {string|null}
	 * @private
	 */
	this._prevCursor = null;
	/**
	 * Merker für Feature-Hover.
	 * @type {ol.Feature|null}
	 * @private
	 */
	this._prevFeature = null;
	/**
	 * Merker für Vertex-Hover.
	 * @type {ol.Feature|null}
	 * @private
	 */
	this._prevVertex = null;
	/**
	 * Parameter zur aktuellen Aktion
	 * @type {Object|null}
	 * @private
	 */
	this._action = null;
	/**
	 * @type {gc.gis.geom.OLGeometryFactory}
	 * @private
	 */
	this._geometryFactory = new gc.gis.geom.OLGeometryFactory();
	/**
	 * Tracking-Layer für die Darstellung der Handles.
	 * @type {gc.gis.layer.OLTrackingLayer}
	 * @private
	 */
	this._handleLayer = new gc.gis.layer.OLTrackingLayer(this.map.getMap(), {
		allowClearDrawingArea: true === opt_options.allowClearDrawingArea,
		clearDrawingAreaAction: opt_options.clearDrawingAreaAction,
		drawOrder: gc.gis.layer.TrackingLayerDrawOrder.SKETCH
	});
	this._handleLayer.setStyle(this._createHandleStyleFunction());
	this.map.getMap().addTrackingLayer(this._handleLayer);

	this._createInteractions();
	this._initEvents();
};
gc.gis.map.OLMapTransformInteraction.prototype = Object.create(gc.gis.map.GCMapTransformInteraction.prototype);

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.setActive = function(state)
{
	state ? this._activate() : this._deactivate();
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.isActive = function()
{
	return this._pointerInteraction.getActive() === true;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.isFeatureSelected = function(feature)
{
	return this._getSelectFeatureIndex(feature.getImpl()) > -1;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.numSelectedFeatures = function ()
{
	return this._selectedFeatures.getLength();
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.getSelectedFeatures = function()
{
	const list = [];
	let container,
		i, ii;
	for (i = 0, ii = this._selectedFeaturesRefs.getLength(); i < ii; i++)
	{
		container = this._selectedFeaturesRefs.item(i);
		list.push(container.gc_feature);
	}

	return list;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.unSelectFeature = function(feature, opt_silent)
{
	return this._unselectFeature(feature.getImpl(), opt_silent) > -1;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.unSelectAllFeatures = function(opt_silent)
{
	let len;
	while ((len = this._selectedFeatures.getLength()) > 0)
	{
		this._unselectFeatureByIndex(len - 1, opt_silent);
	}
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.wasChanged = function(feature)
{
	let container,
		i, ii;

	for (i = 0, ii = this._selectedFeaturesRefs.getLength(); i < ii; i++)
	{
		container = this._selectedFeaturesRefs.item(i);
		if (container.changed && feature === container.gc_feature)
		{
			return true;
		}
	}

	return false;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.numChangedFeatures = function()
{
	return this.getChangedFeatures().length;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapTransformInteraction.prototype.getChangedFeatures = function()
{
	const list = [];
	let container,
		i, ii;

	for (i = 0, ii = this._selectedFeaturesRefs.getLength(); i < ii; i++)
	{
		container = this._selectedFeaturesRefs.item(i);
		if (container.changed)
		{
			list.push(container.gc_feature);
		}
	}

	return list;
};

/**
 * Initialisiert die Eventbehandlungsroutinen.
 *
 * @private
 */
gc.gis.map.OLMapTransformInteraction.prototype._initEvents = function()
{
	const self = this;

	// wenn der Tracking-Layer mit den Handles geleert wird (z.B. Funktion Zeichenfläche leeren),
	// dann auch alle selektierten Features zurücksetzen
	this._handleLayer.on(gc.gis.layer.GCVectorLayerEvent.LAYER_CLEARED, this, function () {
		for (let i = self._selectedFeatures.getLength() - 1; i >= 0; i--)
		{
			self._unselectFeatureByIndex(i);
		}
	});

	// überwachen, welche Features geändert wurden
	this._selectedFeatures.on('add', function (evt) {
		evt.element.getGeometry().once('change', function (evt2) {
			const container = self._selectedFeaturesRefs.item(self._getSelectFeatureIndex(evt.element));
			if (container)
			{
				container.changed = true;
			}
		});
	});
};

/**
 * Führt die Aktivierung der Interaktion aus.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._activate = function()
{
	if (!this.isActive())
	{
		this._pointerInteraction.setActive(true);

		/**
		 * FIXME:
		 * Die De-/Aktivierung der Interaktion passiert über das Entfernen/Hinzufügen der Aktion zum Kartenobjekt.
		 * Die Nutzung der Methode setActive funktioniert zurzeit leider nicht, da sich beide Aktionen dann
		 * sofort gegenseitig deaktivieren würden, siehe gc.gis.OLMap.prototype.__initInteractions.
		 * gc.gis.OLMap.prototype.__initInteractions Bedarf einer Überarbeitung
		 */
		this._ol_map.addInteraction(this._modifyInteraction);

		// Doppelklick beim Bearbeiten unterdrücken => führt andernfalls Zoom-Funktion aus
		const unDblClick = this._ol_map.on('dblclick', function(evt) {
			return false;
		});
		this._eventsKeyList.push(unDblClick);

		this._drawSketch();
		this._handleLayer.setVisible(true);
	}
};

/**
 * Führt die Deaktivierung der Interaktion aus.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._deactivate = function()
{
	if (this.isActive())
	{
		while (this._eventsKeyList.length > 0)
		{
			unByKey(this._eventsKeyList.pop());
		}

		this._pointerInteraction.setActive(false);
		this._ol_map.removeInteraction(this._modifyInteraction);

		this._handleLayer.setVisible(false);
		this._clearSketch();
	}
};

/**
 * Liefert den Index eines selektierten Features aus der Liste.
 *
 * @private
 * @method
 *
 * @param {Feature} feature das betroffene Feature
 * @return {Number} Index des betroffenen Features in der Liste oder -1 falls kein passendes Feature gefunden wurde
 */
gc.gis.map.OLMapTransformInteraction.prototype._getSelectFeatureIndex = function(feature)
{
	const arr = this._selectedFeatures.getArray();
	let i, ii, index = -1;
	for (i = 0, ii = arr.length; i < ii; i++)
	{
		if (arr[i] === feature)
		{
			index = i;
			break;
		}
	}

	return index;
};

/**
 * Selektiert ein Feature und übernimmt es damit in die Zeichenfläche.
 *
 * @private
 * @method
 *
 * @param {Feature} feature das betroffene Feature
 * @return {Number} Index des betroffenen Features in der Liste
 */
gc.gis.map.OLMapTransformInteraction.prototype._selectFeature = function(feature)
{
	const index = this._selectedFeatures.push(feature) - 1,
		gc_feature = new gc.gis.feature.OLFeature(feature);
	let toolbar = this._createFeatureToolbar(gc_feature, index);

	if (toolbar)
	{
		toolbar = new Overlay({
			element: toolbar[0],
			positioning: 'bottom-right',
			offset: [0, -12]
		});
	}

	this._selectedFeaturesRefs.push({
		ol_feature: feature,
		gc_feature: gc_feature,
		toolbar: toolbar,
		changed: false
	});

	if (this._selectStyle)
	{
		feature.setStyle(this._selectStyle);
	}
	this._drawSketch();

	this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.SELECT, {
		feature: gc_feature
	});

	return index;
};

/**
 * Deselektiert ein Feature und entfernt es damit aus der Zeichenfläche.
 *
 * @private
 * @method
 *
 * @param {Feature} feature das betroffene Feature
 * @param {boolean} [opt_silent] Änderung ohne Auslösen des korrespondierenden Events durchführen
 * @return {Number} Index des betroffenen Features in der Liste oder -1 falls kein passendes Feature gefunden wurde
 */
gc.gis.map.OLMapTransformInteraction.prototype._unselectFeature = function(feature, opt_silent)
{
	const index = this._getSelectFeatureIndex(feature);

	return (this._unselectFeatureByIndex(index, opt_silent) ? index : -1);
};

/**
 * Deselektiert ein Feature und entfernt es damit aus der Zeichenfläche anhand seines Index in der Referenzliste.
 *
 * @private
 * @method
 *
 * @param {Number} index der Index des betroffenen Features
 * @param {boolean} [opt_silent] Änderung ohne Auslösen des korrespondierenden Events durchführen
 * @return {Object|undefined} das betroffene Feature oder <code>undefined</code> wenn der Index ungültig ist
 */
gc.gis.map.OLMapTransformInteraction.prototype._unselectFeatureByIndex = function(index, opt_silent)
{
	let feature = undefined,
		container = undefined;

	if (index >= 0)
	{
		feature = this._selectedFeatures.removeAt(index);
	}

	if (feature !== undefined)
	{
		// explizit aufrufen, damit auch die Toolbars korrekt bereinigt werden und zwar bevor, das FeatureRef gelöscht wird
		this._clearSketch();
		container = this._selectedFeaturesRefs.removeAt(index);

		if (this._selectStyle)
		{
			feature.setStyle(null);
		}
		this._drawSketch();

		if (opt_silent !== true)
		{
			this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.UNSELECT, {
				feature: container.gc_feature
			});
		}
	}

	return container;
};

/**
 * Entfernt ein Feature aus dem Bearbeitungsmodus und liefert den Index des Features aus der Liste.
 *
 * @param {Feature} feature
 * @return {int}
 * @private
 */
gc.gis.map.OLMapTransformInteraction.prototype._removeFeature = function(feature)
{
	const index = this._getSelectFeatureIndex(feature);

	return (this._removeFeatureByIndex(index) ? index : -1);
};

/**
 * Entfernt ein Feature aus dem Bearbeitungsmodus anhand seines Index aus der Liste.
 *
 * @param {Number} index
 * @return {Object|undefined} das betroffene Feature  oder <code>undefined</code> wenn der Index ungültig ist
 * @private
 */
gc.gis.map.OLMapTransformInteraction.prototype._removeFeatureByIndex = function(index)
{
	const container = this._unselectFeatureByIndex(index, true);

	if (container !== undefined)
	{
		this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.REMOVEDRAWAREA, {
			feature: container.gc_feature
		});
	}

	return container;
};

/**
 * Initialisiert die notwendigen Interaktionen für die Geometrie-Bearbeitungsfunktion.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._createInteractions = function()
{
	const self = this;
	let props;

	// Reihenfolge, erst Pointer, dann Modify ist für weitere Verarbeitung insbesondere Reihenfolge Event-Verarbeitung wichtig
	if (!this._pointerInteraction)
	{
		props = {
			handleDownEvent: function (evt) {
				return self._doPointerDown(evt);
			},
			handleDragEvent: function (evt) {
				return self._doPointerDrag(evt);
			},
			handleMoveEvent: function (evt) {
				return self._doPointerMove(evt);
			},
			handleUpEvent: function (evt) {
				return self._doPointerUp(evt);
			}
		};

		this._pointerInteraction = new PointerInteraction(props);
		this._pointerInteraction.set('_is_gc', false, true);
		this._ol_map.addInteraction(this._pointerInteraction);
	}

	if (!this._modifyInteraction)
	{
		props = {
			//features: this._selectedFeatures,
			source: this._modifyInteractionSource,
			style: this._createModifyStyleFunction(),
			pixelTolerance: this._DEF_PIXEL_TOLERANCE,
			deleteCondition: function(evt) {
				return olCondition.singleClick(evt);
			}
		};

		this._modifyInteraction = new Modify(props);
		this._modifyInteraction.set('_is_gc', false, true);
		// Hack: nicht zur Karte hinzufügen, da sonst Pointer-Interaktion wieder deaktiviert würde

		this._modifyInteraction.on('modifystart', function(evt) {
			self._doModifyStart(evt);
		});
		this._modifyInteraction.on('modifyend', function(evt) {
			self._doModifyEnd(evt);
		});
	}
};

/**
 * Liefert ein Feature anhand einer Pixel-Koordinate.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._getFeatureAtPixel = function (pixel)
{
	const self = this;

	// // forEach endet, sobald ein truthy Wert zurückgegeben wird
	// return this._ol_map.forEachFeatureAtPixel(pixel,
	// 	function (feature, layer) {
	// 		// Feature zu Pixel suchen, dabei Box-Handle nicht berücksichtigen (liegt über dem eigentlichen Feature)
	// 		return (
	// 			(layer === self._handleLayer.getImpl()
	// 				&& feature.get('handle') === gc.gis.map.OLMapTransformInteractionHandles.BOX)
	// 			||
	// 			(layer && self._layerFilter && layer !== self._layerFilter.getImpl()
	// 				&& layer !== self._handleLayer.getImpl()))
	// 				? undefined : {feature: feature, layer: layer};
	// 	})
	// || null;

	const result = {};
	// forEach endet, sobald ein truthy Wert zurückgegeben wird
	this._ol_map.forEachFeatureAtPixel(pixel,
		function (feature, layer) {
			// Feature zu Pixel suchen, dabei Box-Handle nicht berücksichtigen (liegt über dem eigentlichen Feature)
			if (!(
				(layer === self._handleLayer.getImpl()
					&& feature.get('handle') === gc.gis.map.OLMapTransformInteractionHandles.BOX)
				||
				(layer && self._layerFilter && layer !== self._layerFilter.getImpl()
					&& layer !== self._handleLayer.getImpl()))
			)
			{
				if (layer && !result.layer) // nur den ersten Treffer nehmen, das sind Handles
				{
					result.feature = feature;
					result.layer = layer;
				}
				else
				{
					result.vertex = feature.getGeometry();
				}
			}
		});

	return (result.feature || result.vertex) ? result : null;
};

/**
 * Sucht ein Feature innerhalb der ausgewählten Features anhand eines Pixels und ggf. einer Toleranz.
 *
 * @param pixel
 * @param tolerance
 * @return {Feature|null}
 * @private
 */
gc.gis.map.OLMapTransformInteraction.prototype._getSelectedFeatureAtPixel = function (pixel, tolerance)
{
	tolerance = tolerance || 0;

	const coord =  this._ol_map.getCoordinateFromPixel(pixel);
	let ll = [pixel[0] - tolerance, pixel[1] - tolerance];
	let ur = [pixel[0] + tolerance, pixel[1] + tolerance];
	let features, i, ii, p, dx, dy;

	ll = this._ol_map.getCoordinateFromPixel(ll);
	ur = this._ol_map.getCoordinateFromPixel(ur);
	features = this._modifyInteractionSource.getFeaturesInExtent(olExtent.boundingExtent([ll, ur]));

	for (i = 0, ii = features.length; i < ii; i++)
	{
		p = features[0].getGeometry().getClosestPoint(coord);
		p = this._ol_map.getPixelFromCoordinate(p);

		dx = pixel[0] - p[0];
		dy = pixel[1] - p[1];

		if (Math.sqrt(dx*dx + dy*dy) <= tolerance)
		{
			return features[i];
		}
	}

	return null;
};

/**
 * Zeichnet die Handles der Transformationsfunktionen.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._drawSketch = function ()
{
	this._clearSketch();

	for (let i = 0, ii = this._selectedFeaturesRefs.getLength(); i < ii; i++)
	{
		const container = this._selectedFeaturesRefs.item(i);
		const isPoint = container.ol_feature.getGeometry().getType() === 'Point';
		const extent = container.ol_feature.getGeometry().getExtent();
		const geom = fromExtent(extent);
		const coords = geom.getCoordinates()[0];
		const action = this._action ? this._action.action : null;
		let feature, j,	p;

		// Umgebungsrechteck
		if (!isPoint)
		{
			feature = this._handleLayer.addGeometry(new gc.gis.geom.OLPolygon(geom));
			feature.setAttribute('handle', gc.gis.map.OLMapTransformInteractionHandles.BOX);
			feature.setAttribute('featureIndex', i);

			if (action !== gc.gis.map.OLMapTransformInteractionHandles.ROTATE)
			{
				// Handles zum Ziehen
				for (j = 0; j < coords.length - 1; j++)
				{
					// Ecken zum Skalieren
					p = new Point(coords[j]);
					feature = this._handleLayer.addGeometry(new gc.gis.geom.OLPoint(p));
					feature.setAttribute('handle', gc.gis.map.OLMapTransformInteractionHandles.SCALE);
					feature.setAttribute('node', j);
					feature.setAttribute('featureIndex', i);

					// Mittel zum Stretchen
					p = new Point([
						(coords[j][0] + coords[j+1][0]) / 2,
						(coords[j][1] + coords[j+1][1]) / 2
					]);

					feature = this._handleLayer.addGeometry(new gc.gis.geom.OLPoint(p));
					feature.setAttribute('handle', j%2 ? gc.gis.map.OLMapTransformInteractionHandles.SCALE_H :
						gc.gis.map.OLMapTransformInteractionHandles.SCALE_V);
					feature.setAttribute('node', j);
					feature.setAttribute('featureIndex', i);
				}

				// Mitte zum Verschieben
				p = new Point([
					(coords[0][0] + coords[2][0]) / 2,
					(coords[0][1] + coords[2][1]) / 2
				]);

				feature = this._handleLayer.addGeometry(new gc.gis.geom.OLPoint(p));
				feature.setAttribute('handle', gc.gis.map.OLMapTransformInteractionHandles.TRANSLATE);
				feature.setAttribute('featureIndex', i);
			}
			else
			{
				// Schwerpunkt der Rotation darstellen
				p = new Point(this._action.center);

				feature = this._handleLayer.addGeometry(new gc.gis.geom.OLPoint(p));
				feature.setAttribute('handle', gc.gis.map.OLMapTransformInteractionHandles.TRANSLATE);
				feature.setAttribute('featureIndex', i);
			}
		}

		// Rotieren (immer Mitte unten)
		p = new Point([
			(coords[0][0] + coords[3][0]) / 2,
			(coords[0][1] + coords[3][1]) / 2
		]);
		feature = this._handleLayer.addGeometry(new gc.gis.geom.OLPoint(p));
		feature.setAttribute('handle', gc.gis.map.OLMapTransformInteractionHandles.ROTATE);
		feature.setAttribute('featureIndex', i);

		// Overlay mit Toolbar
		if (container.toolbar)
		{
			container.toolbar.setPosition(coords[2]);
			this._ol_map.addOverlay(container.toolbar);
		}
	}
};

/**
 * Setzt die Handles der Transformationsfunktionen zurück.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._clearSketch = function ()
{
	const self = this;
	// Features nicht über clear leeren => sonst wird Clear-Event ausgelöst, was auch die selektiereten Objekte
	// zurücksetzt, beim Entfernen der einzelnen Features wird dieses Event nicht gefeuert. Siehe Ereignisbehandlung für
	// gc.gis.layer.GCVectorLayerEvent.LAYER_CLEARED am _handleLayer
	// this._handleLayer.clear();
	this._handleLayer.removeFeatures(this._handleLayer.getFeatures());

	this._selectedFeaturesRefs.forEach(function (item) {
		if (item && item.toolbar)
		{
			self._ol_map.removeOverlay(item.toolbar);
		}
	});
};

/**
 * Erzeugt eine Toolbar für ein ausgewähltes Feature.
 *
 * @protected
 * @method
 *
 * @param {gc.gis.feature.GCFeature} feature betroffenes Feature
 * @return {jQuery}
 */
gc.gis.map.OLMapTransformInteraction.prototype._createFeatureToolbar = function (feature)
{
	const self = this,
		buttons = [];
	let btn;

	if (this._toolbarButtons & gc.gis.map.GCMapTransformInteractionButtons.STYLE)
	{
		btn = jQuery('<button>', {
			'class': 'gc-map-control-button',
			title: 'Darstellung bearbeiten',
			html: '<span>Darstellung bearbeiten</span>',
			click: function(evt) {
				evt.preventDefault();
				try
				{
					// TODO
				}
				catch( err )
				{
					gc.gis.iface.log.error(self, err);
				}
				jQuery(this).blur();
			}
		});
		btn.prepend( jQuery('<span>', {
			'class': 'gc-icon glyphicon glyphicon-tint'
		}) );
		buttons.push(btn);
	}

	if (this._toolbarButtons & gc.gis.map.GCMapTransformInteractionButtons.SAVE)
	{
		btn = jQuery('<button>', {
			'class': 'gc-map-control-button',
			title: 'Änderung Speichern',
			html: '<span>Änderung Speichern</span>',
			click: function (evt) {
				evt.preventDefault();
				try
				{
					if (self._saveAction instanceof gc.gis.action.GCAbstractAction)
					{
						self._saveAction.args = [feature];
						self._saveAction.execute(self);
					}
					else
					{
						self._unselectFeature(feature.getImpl());
					}
				}
				catch (err)
				{
					gc.gis.iface.log.error(self, err);
				}
				jQuery(this).blur();
			}
		});
		btn.prepend(jQuery('<span>', {
			'class': 'gc-icon glyphicon glyphicon-ok'
		}));
		buttons.push(btn);
	}

	if (this._toolbarButtons & gc.gis.map.GCMapTransformInteractionButtons.DELETE)
	{
		btn = jQuery('<button>', {
			'class': 'gc-map-control-button',
			title: 'Objekt löschen',
			html: '<span>Objekt löschen</span>',
			click: function (evt) {
				evt.preventDefault();
				try
				{
					if (self._deleteAction instanceof gc.gis.action.GCAbstractAction)
					{
						self._deleteAction.args = [feature];
						self._deleteAction.execute(self);
					}
					else
					{
						self._unselectFeature(feature.getImpl());
					}
				}
				catch (err)
				{
					gc.gis.iface.log.error(self, err);
				}
				jQuery(this).blur();
			}
		});
		btn.prepend(jQuery('<span>', {
			'class': 'gc-icon glyphicon glyphicon-trash'
		}));
		buttons.push(btn);
	}

	if (this._toolbarButtons & gc.gis.map.GCMapTransformInteractionButtons.CLEAR)
	{
		btn = jQuery('<button>', {
			'class': 'gc-map-control-button',
			title: 'von Zeichenfläche entfernen',
			html: '<span>von Zeichenfläche entfernen</span>',
			click: function(evt) {
				evt.preventDefault();
				try
				{
					if (self._removeDrawAreaAction instanceof gc.gis.action.GCAbstractAction)
					{
						self._removeDrawAreaAction.args = [feature];
						self._removeDrawAreaAction.execute(self);
					}
					else
					{
						self._removeFeature(feature.getImpl());
					}
				}
				catch( err )
				{
					gc.gis.iface.log.error(self, err);
				}
				jQuery(this).blur();
			}
		});
		btn.prepend( jQuery('<span>', {
			'class': 'gc-icon glyphicon glyphicon-erase'
		}) );
		buttons.push(btn);
	}

	if (this._toolbarButtons & gc.gis.map.GCMapTransformInteractionButtons.CANCEL)
	{
		btn = jQuery('<button>', {
			'class': 'gc-map-control-button',
			title: 'Bearbeitung beenden',
			html: '<span>Bearbeitung beenden</span>',
			click: function(evt) {
				evt.preventDefault();
				try
				{
					if (self._cancelAction instanceof gc.gis.action.GCAbstractAction)
					{
						self._cancelAction.args = [feature];
						self._cancelAction.execute(self);
					}
					else
					{
						self._unselectFeature(feature.getImpl());
					}
				}
				catch(err)
				{
					gc.gis.iface.log.error(self, err);
				}
				jQuery(this).blur();
			}
		});
		btn.prepend( jQuery('<span>', {
			'class': 'gc-icon glyphicon glyphicon-remove'
		}) );
		buttons.push(btn);
	}

	if (buttons.length > 0)
	{
		const element = jQuery('<div>', {
			class: 'gc-map-control gc-map-control-feature-editing'
		});

		for (let i = 0, ii = buttons.length; i < ii; i++)
		{
			element.append(buttons[i]);
		}

		return element;
	}

	return null;
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._doModifyStart = function (evt)
{
	this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.MODIFY_START, {
		// TODO
	});
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._doModifyEnd = function (evt)
{
	this._drawSketch();

	this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.MODIFY_END, {
		// TODO
	});
};

/**
 * @private
 * @method
 * @return {boolean} `true` to start the drag sequence.
 */
gc.gis.map.OLMapTransformInteraction.prototype._doPointerDown = function (evt)
{
	const result = this._getFeatureAtPixel(evt.pixel);
	if (result)
	{
		if (result.layer === this._handleLayer.getImpl())
		{
			// Handle angeklickt
			const container = this._selectedFeaturesRefs.item(result.feature.get('featureIndex'));
			if (container)
			{
				this._action = {
					action: result.feature.get('handle'),
					feature: container.ol_feature,
					geometry: container.ol_feature.getGeometry(),
					featureWrap: container.gc_feature,
					center: olExtent.getCenter(container.ol_feature.getGeometry().getExtent()),
					//handleFeature: result.feature,
					startCoord: evt.coordinate,
					startPixel: evt.pixel
				};
				return true;    // `true` to start the drag sequence.
			}
		}
		else if (result.layer)  // wenn kein Layer definiert => Overlay z.B. Punkte bei Stützpunkte bearbeiten
		{
			if (this._unselectFeature(result.feature) < 0)
			{
				this._selectFeature(result.feature);
			}
		}
	}

	return false;
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._doPointerDrag = function (evt)
{
	const geom = this._action.geometry.clone();   // nicht die Geometry vom Feature nehmen, da diese während der Transformation geändert wird

	switch (this._action.action)
	{
		case gc.gis.map.OLMapTransformInteractionHandles.TRANSLATE:
			const deltaX = evt.coordinate[0] - this._action.startCoord[0];
			const deltaY = evt.coordinate[1] - this._action.startCoord[1];
			geom.translate(deltaX, deltaY);

			this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.TRANSLATING, {
				feature: this._action.featureWrap,
				delta: [deltaX, deltaY]
			});

			break;

		case gc.gis.map.OLMapTransformInteractionHandles.SCALE:
		case gc.gis.map.OLMapTransformInteractionHandles.SCALE_V:
		case gc.gis.map.OLMapTransformInteractionHandles.SCALE_H:
			let sx = (evt.coordinate[0] - this._action.center[0]) / (this._action.startCoord[0] - this._action.center[0]);
			let sy = (evt.coordinate[1] - this._action.center[1]) / (this._action.startCoord[1] - this._action.center[1]);

			if (this._action.action === gc.gis.map.OLMapTransformInteractionHandles.SCALE_V)
			{
				sy = 1.0;
			}
			else if (this._action.action === gc.gis.map.OLMapTransformInteractionHandles.SCALE_H)
			{
				sx = 1.0;
			}
			// TODO aspectRatio steuern
			// else if ("aspectRatio aktiv??? z.B. auch über Shift")
			// {
			// 	sx = sy = Math.min(sy, sy);
			// }

			geom.scale(sx, sy, this._action.center);

			this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.SCALING, {
				feature: this._action.featureWrap,
				scaleFactor: [sx, sy],
				center: this._action.center
			});

			break;

		case gc.gis.map.OLMapTransformInteractionHandles.ROTATE:

			const alpha1 = Math.atan2(
				this._action.center[1] - this._action.startCoord[1],
				this._action.center[0] - this._action.startCoord[0]
			);
			const alpha2 = Math.atan2(
				this._action.center[1] - evt.coordinate[1],
				this._action.center[0] - evt.coordinate[0]
			);

			geom.rotate(alpha2 - alpha1, this._action.center);

			this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.ROTATING, {
				feature: this._action.featureWrap,
				angle: alpha2 - alpha1,
				center: this._action.center
			});

			break;
	}

	this._action.feature.getGeometry().changed();   // explizit das Änderungs-Event für die Geometrie triggern (dadurch wird das Feature in die Liste der geänderten Features aufgenommen (siehe _initEvents())
	this._action.feature.setGeometry(geom);
	// FIXME: hier hat das ganze "Gewrappe" Grenzen, die gekapselte Geometrie im featureWrap bekommt nicht mit, wenn die korrespondierende Geometrie im des OL-Features geändert wird
	this._action.featureWrap.setGeometry(this._geometryFactory.createGeometry(geom));

	this._drawSketch();
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._doPointerMove = function (evt)
{
	const target = this._ol_map.getTargetElement();
	const result = this._getFeatureAtPixel(evt.pixel);

	if (result)
	{
		if (this._prevCursor === null)
		{
			this._prevCursor = target.style.cursor;
		}

		let cursor = 'pointer';
		// Cursor für Handles
		if (result.layer === this._handleLayer.getImpl())
		{
			switch (result.feature.get('handle'))
			{
				case gc.gis.map.OLMapTransformInteractionHandles.TRANSLATE:
				case gc.gis.map.OLMapTransformInteractionHandles.ROTATE:
					cursor = 'move';
					break;
				case gc.gis.map.OLMapTransformInteractionHandles.SCALE_H:
				case gc.gis.map.OLMapTransformInteractionHandles.SCALE_V:
					switch (result.feature.get('node'))
					{
						case 0: cursor = 'e-resize'; break;
						case 1: cursor = 'n-resize'; break;
						case 2: cursor = 'w-resize'; break;
						case 3: cursor = 's-resize'; break;
					}
					break;
				case gc.gis.map.OLMapTransformInteractionHandles.SCALE:
					switch (result.feature.get('node'))
					{
						case 0: cursor = 'sw-resize'; break;
						case 1: cursor = 'se-resize'; break;
						case 2: cursor = 'ne-resize'; break;
						case 3: cursor = 'nw-resize'; break;
					}
					break;
			}
		}
		else if (result.vertex)
		{
			cursor = 'grab';

			const isNew = result.feature === undefined ||
				!gc.gis.OLUtil.isVertex(result.vertex.getCoordinates(), result.feature.getGeometry());

			this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.HOVER_VERTEX, {
				coordinate: result.vertex.getCoordinates(),
				isNew: isNew
			});
			this._prevVertex = result.vertex;
		}
		else if (result.feature)
		{
			if (result.feature !== this._prevFeature)
			{
				// Event: Feature verlassen und ein neues betreten
				if (this._prevFeature !== null)
				{
					this._fireFeatureEnterLeaveEvent(this._prevFeature, true);
				}
				this._fireFeatureEnterLeaveEvent(result.feature, false);

				if (this._hoverStyle && this._prevFeature !== null
					&& this._getSelectFeatureIndex(this._prevFeature) < 0)
				{
					this._prevFeature.setStyle(null);
				}

				this._prevFeature = result.feature;
				if (this._hoverStyle && this._getSelectFeatureIndex(result.feature) < 0)
				{
					result.feature.setStyle(this._hoverStyle);
				}
			}
		}

		target.style.cursor = cursor;
	}
	else
	{
		if (this._prevCursor !== null)
		{
			target.style.cursor = this._prevCursor;
		}
		this._prevCursor = null;

		if (this._prevFeature !== null)
		{
			this._fireFeatureEnterLeaveEvent(this._prevFeature, true);

			if (this._getSelectFeatureIndex(this._prevFeature) < 0)
			{
				this._prevFeature.setStyle(null);
			}
		}
		this._prevFeature = null;

		if (this._prevVertex !== null)
		{
			this._fireEvent(gc.gis.map.GCMapTransformInteractionEvent.LEAVE_VERTEX, {});
			this._prevVertex = null;
		}
	}
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._doPointerUp = function (evt)
{
	this._action = null;
	this._drawSketch();
};

/**
 * Erzeugt eine Funktion, welche die Styles zum Darstellen der Stützpunktbearbeitung liefert.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._createModifyStyleFunction = function ()
{
	const self = this;

	const red = [255, 0, 0];
	const green = [0, 255, 0];

	const circleEdit = new Circle({
		fill: new Fill({
			color: red.concat(0.3)
		}),
		stroke: new Stroke({
			color: red.concat(1.0),
			width: 2
		}),
		radius: 8
	});

	const circleNew = new Circle({
		fill: new Fill({
			color: green.concat(0.3)
		}),
		stroke: new Stroke({
			color: green.concat(1.0),
			width: 2
		}),
		radius: 8
	});

	const styles = {};
	styles['edit'] = new Style({
		image: circleEdit
	});
	styles['new'] = new Style({
		image: circleNew
	});

	return function (feature, resolution) {
		const refCoord = feature.getGeometry().getCoordinates();
		let style = styles.new;
		let i, j, geom, coords;

		for (i=0; i<self._selectedFeatures.getLength() && style === styles.new; i++)
		{
			geom = self._selectedFeatures.item(i).getGeometry();
			//if (geom.intersectsCoordinate(refCoord))  // liefert false, wenn Coordinate auf dem Rand der Geometrie liegt
			{
				// coords = geom.getFlatCoordinates();
				coords = gc.gis.OLUtil.getFlatCoordinates(geom);
				for (j=0; j<coords.length - 1; j++)
				{
					if (refCoord[0] === coords[j] && refCoord[1] === coords[j+1])
					{
						style = styles.edit;
						break;
					}
				}
			}
		}

		return style;
	};
};

/**
 * Löst eine Ereignisbehandlungsroutine aus die anzeigt, das ein Feature mit der Maus betreten oder verlassen wurde.
 *
 * @private
 * @method
 * @param {Feature} feature
 * @param {boolean} leave
 */
gc.gis.map.OLMapTransformInteraction.prototype._fireFeatureEnterLeaveEvent = function (feature, leave)
{
	const featureIdx = this._getSelectFeatureIndex(feature);
	let container, evtFeature;

	// wenn das Feature selektiert ist, dann existiert ein Wrapper andernfalls einen erzeugen
	if (featureIdx >= 0)
	{
		container = this._selectedFeaturesRefs.item(featureIdx);
		evtFeature = container.gc_feature;
	}
	else
	{
		evtFeature = new gc.gis.feature.OLFeature(this._prevFeature);
	}

	this._fireEvent(
		leave ? gc.gis.map.GCMapTransformInteractionEvent.LEAVE : gc.gis.map.GCMapTransformInteractionEvent.ENTER,
		{
			feature: evtFeature,
			selected: featureIdx >= 0
		}
	);
};

/**
 * Erzeugt eine Funktion, welche die Styles zum Darstellen der Handles für die Transformationsfunktionen liefert.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapTransformInteraction.prototype._createHandleStyleFunction = function ()
{
	// Style
	const color = [255, 0, 0];
	const white = [255, 255, 255];
	const stroke = new Stroke({
		color: color.concat(1.0),
		width: 1
	});
	const stroke_dashed = new Stroke({
		color: color.concat(1.0),
		width: 1,
		lineDash:[4, 4]
	});
	const fill_empty = new Fill({
		color: white.concat(0.0)
	});
	const fill = new Fill({
		color: white.concat(0.8)
	});

	const rect_big = new RegularShape({
		fill: fill,
		stroke: stroke,
		radius: 15,
		points: 4,
		angle: Math.PI/4
	});
	const rect_small = new RegularShape({
		fill: fill,
		stroke: stroke,
		radius: 10,
		points: 4,
		angle: Math.PI/4
	});
	const circle = new Circle({
		fill: fill,
		stroke: stroke,
		radius: 12
	});
	circle.getAnchor()[1] = -10;

	const styles = {};
	styles[gc.gis.map.OLMapTransformInteractionHandles.BOX] = new Style({
		image: rect_big,
		stroke: stroke_dashed,
		fill: fill_empty
	});
	styles[gc.gis.map.OLMapTransformInteractionHandles.SCALE] = new Style({
		image: rect_big,
		stroke: stroke,
		fill: fill
	});
	styles[gc.gis.map.OLMapTransformInteractionHandles.SCALE_H] = new Style({
		image: rect_small,
		stroke: stroke,
		fill: fill
	});
	styles[gc.gis.map.OLMapTransformInteractionHandles.SCALE_V] =
		styles[gc.gis.map.OLMapTransformInteractionHandles.SCALE_H];
	// TODO anderes Symbol für Rotieren
	styles[gc.gis.map.OLMapTransformInteractionHandles.ROTATE] = new Style({
		image: circle,
		stroke: stroke,
		fill: fill
	});
	// TODO Kreuz im Mittelpunkt darstellen
	styles[gc.gis.map.OLMapTransformInteractionHandles.TRANSLATE] = new Style({
		image: rect_big,
		stroke: stroke,
		fill: fill
	});

	return function(feature, resolution) {
		const key = feature.get('handle');
		return styles[key];
	}
};
